import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { Mail, Phone, School, Landmark } from "lucide-react"
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar"
import { useState , useEffect } from 'react'
import { Header } from '@/components/layout/header'
import { Search } from '@/components/search'
import { ThemeSwitch } from '@/components/theme-switch'
import { ProfileDropdown } from '@/components/profile-dropdown'
import { TopNav } from '@/components/layout/top-nav'
import { Main } from "@/components/layout/main"
import apiClient from '@/lib/axios'

interface Egitmen {
  _id: string;
  adSoyad: string;
  mezuniyet: string;
  eposta: string;
  telefon: string;
  profilLink: string;
  profilResimUrl: string;
  unvan: string;
  departmanId?: Array<{
    _id: string;
    ad: string;
  }>;
}

interface Departman {
  _id: string;
  ad: string;
}

const topNav = [
    {
        title: "Ana Sayfa",
        href: "/",
        isActive: false,
    },
    {
        title: "Bölümler",
        href: "/bolumler",
        isActive: false,
    },
    {
        title: "Eğitmenler",
        href: "/egitmenler",
        isActive: true,
    },
]

const getImageUrl = (profilResimUrl: string) => {
  return profilResimUrl || '';
};

export default function EgitmenlerPage() {
  const [departmanlar, setDepartmanlar] = useState<Departman[]>([]);
  const [egitmenlerByDepartman, setEgitmenlerByDepartman] = useState<Record<string, Egitmen[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [imageLoadingStates, setImageLoadingStates] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch all departments
        const departmanResponse = await apiClient.get('/departman');
        const departmanList = departmanResponse.data;
        setDepartmanlar(departmanList);

        // Fetch instructors for each department
        const egitmenlerMap: Record<string, Egitmen[]> = {};
        
        await Promise.all(
          departmanList.map(async (departman: Departman) => {
            try {
              const egitmenResponse = await apiClient.get(`/egitmen/departman/${departman._id}`);
              egitmenlerMap[departman._id] = egitmenResponse.data;
              
              // Initialize image loading states for all instructors
              const initialStates: Record<string, boolean> = {};
              egitmenResponse.data.forEach((egitmen: Egitmen) => {
                initialStates[egitmen._id] = true; // true means loading
              });
              setImageLoadingStates(prev => ({ ...prev, ...initialStates }));
            } catch (err) {
              egitmenlerMap[departman._id] = [];
            }
          })
        );

        setEgitmenlerByDepartman(egitmenlerMap);
      } catch (err) {
        setError('Eğitmenler yüklenirken bir hata oluştu.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
        <>
            <Header>
                <TopNav links={topNav} />
                <div className='ml-auto flex items-center space-x-4'>
                    <Search />
                    <ThemeSwitch />
                    <ProfileDropdown />
                </div>
            </Header>
            <Main className="peer-[.header-fixed]/header:mt-16 px-4 py-6">
                <div className="flex justify-between items-center mb-6">
                    <div>
              <Skeleton className="h-8 w-64 mb-2" />
              <Skeleton className="h-4 w-96" />
                    </div>
                </div>
          <div className="space-y-8">
            {[...Array(3)].map((_, i) => (
              <div key={i}>
                <Skeleton className="h-8 w-48 mb-6" />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[...Array(3)].map((_, j) => (
                    <Card key={j}>
                            <CardHeader>
                        <div className="flex items-center gap-4">
                          <Skeleton className="h-20 w-20 rounded-full" />
                          <div className="flex-1">
                            <Skeleton className="h-6 w-3/4 mb-2" />
                            <Skeleton className="h-4 w-1/2" />
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent>
                        <div className="space-y-3">
                          <Skeleton className="h-4 w-full" />
                          <Skeleton className="h-4 w-full" />
                          <Skeleton className="h-4 w-2/3" />
                                </div>
                            </CardContent>
                            <CardFooter>
                        <Skeleton className="h-10 w-full" />
                            </CardFooter>
                        </Card>
                    ))}
                                    </div>
                                </div>
                    ))}
                </div>
        </Main>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header>
          <TopNav links={topNav} />
          <div className='ml-auto flex items-center space-x-4'>
            <Search />
            <ThemeSwitch />
            <ProfileDropdown />
                                        </div>
        </Header>
        <Main className="peer-[.header-fixed]/header:mt-16 px-4 py-6">
          <div className="text-center py-8 text-red-500">{error}</div>
        </Main>
      </>
    );
  }

  return (
    <>
      <Header>
        <TopNav links={topNav} />
        <div className='ml-auto flex items-center space-x-4'>
          <Search />
          <ThemeSwitch />
          <ProfileDropdown />
                                    </div>
      </Header>
      <Main className="peer-[.header-fixed]/header:mt-16 px-4 py-6">
        <div className="flex justify-between items-center mb-6">
                                        <div>
            <h1 className="text-2xl font-bold tracking-tight">Akademik Personel</h1>
            <p className="text-muted-foreground">Yüksekokulumuzda bulunan tüm akademik personelin listesi</p>
                                        </div>
                                    </div>

        {departmanlar.map((departman) => {
          const egitmenler = egitmenlerByDepartman[departman._id] || [];
          if (egitmenler.length === 0) return null;

          return (
            <div key={departman._id}>
              <h6 className="text-2xl font-semibold mb-6 mt-8 pb-2 border-b">{departman.ad}</h6>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {egitmenler.map((egitmen) => {
                  const imageUrl = getImageUrl(egitmen.profilResimUrl);
                  const isImageLoading = imageLoadingStates[egitmen._id] !== false;
                  
                  return (
                  <Card key={egitmen._id} className="relative">
                            <CardHeader>
                      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                        {isImageLoading && (
                          <Skeleton className="h-20 w-20 shrink-0 rounded-full" />
                        )}
                        <Avatar className={`h-20 w-20 shrink-0 ${isImageLoading ? 'hidden' : ''}`}>
                          <AvatarImage 
                            src={imageUrl} 
                            alt={egitmen.adSoyad}
                          />
                          <AvatarFallback>{egitmen.adSoyad.split(' ').map(n => n[0]).join('')}</AvatarFallback>
                        </Avatar>
                        {/* Preload image to detect when it's loaded */}
                        {isImageLoading && imageUrl && (
                          <img
                            src={imageUrl}
                            alt=""
                            className="hidden"
                            onLoad={() => {
                              setImageLoadingStates(prev => ({ ...prev, [egitmen._id]: false }));
                            }}
                            onError={() => {
                              setImageLoadingStates(prev => ({ ...prev, [egitmen._id]: false }));
                            }}
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <CardTitle className="text-lg sm:text-xl break-words">{egitmen.adSoyad}</CardTitle>
                          <CardDescription className="break-words">{egitmen.unvan}</CardDescription>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-2">
                        <div className="flex items-start sm:items-center space-x-2">
                          <Landmark className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5 sm:mt-0" />
                          <span className="text-muted-foreground break-words min-w-0">Pazaryeri Meslek Yüksekokulu</span>
                                    </div>
                        <div className="flex items-start sm:items-center space-x-2">
                          <School className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5 sm:mt-0" />
                          <span className="text-muted-foreground break-words min-w-0">{egitmen.mezuniyet}</span>
                                    </div>
                        <div className="flex items-start sm:items-center space-x-2">
                          <Mail className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5 sm:mt-0" />
                                        <a
                            href={`mailto:${egitmen.eposta}`}
                            className="text-muted-foreground hover:text-primary transition-colors break-words min-w-0"
                                        >
                            {egitmen.eposta}
                                        </a>
                                    </div>
                        <div className="flex items-start sm:items-center space-x-2">
                          <Phone className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5 sm:mt-0" />
                                        <a
                            href={`tel:${egitmen.telefon.replace(/\s+/g, '')}`}
                            className="text-muted-foreground hover:text-primary transition-colors break-words min-w-0"
                                        >
                            {egitmen.telefon}
                                        </a>
                                    </div>
                                </div>
                            </CardContent>
                            <CardFooter>
                                <a
                        href={egitmen.profilLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-full"
                                >
                                    <Button variant="outline" className="w-full">
                                        Profili Görüntüle
                                    </Button>
                                </a>
                            </CardFooter>
                        </Card>
                  );
                })}
                </div>
                                        </div>
          );
        })}
            </Main>
        </>
    )
}